// Core packages
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { AfterViewInit, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
// Third party packages
import { MatDialog } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { finalize, take } from "rxjs/operators";
// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { DocumentTemplateDocsDialogComponent } from "src/app/modules/settings/components/document-template-docs-dialog/document-template-docs-dialog.component";
import { DocumentTemplateMoveDialogComponent, } from "src/app/modules/settings/components/document-template-move-dialog/document-template-move-dialog.component";
import { DocumentTemplateFieldFactory, } from "src/app/modules/settings/document-template.fields";
import { DocumentTemplateService } from "src/app/modules/settings/document-template.service";
import { findLabelConflict, suggestAreaCode, } from "src/app/modules/settings/document-template.utils";
import { ConfirmDialogComponent } from "src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ConfigService } from "src/app/shared/services/config.service";
import { ThemeService } from "src/app/shared/services/theme.service";
/**
 * Document template management, three-collections back-end
 * (/api/v2/document-template: areas -> contents -> docs).
 *
 * Loading is per level and never fetches the whole tree to feed the UI:
 * areas on mount, sections when an area is picked, documents when the dialog opens.
 * Ordering is persisted on drop through the `position` field, so the lists have no
 * "save" button of their own.
 *
 * @since 3.0.0
 */
var DocumentTemplateComponent = /** @class */ (function () {
    // END - New content (section) form
    /**
     * Class constructor
     */
    function DocumentTemplateComponent(authService, configService, cookieService, documentTemplateService, themeService, toastrService, translateService, dialog) {
        this.authService = authService;
        this.configService = configService;
        this.cookieService = cookieService;
        this.documentTemplateService = documentTemplateService;
        this.themeService = themeService;
        this.toastrService = toastrService;
        this.translateService = translateService;
        this.dialog = dialog;
        this.subscriptions = [];
        this.loggedUserPermissions = [];
        this.lang = this.translateService.currentLang || this.translateService.defaultLang;
        // BEGIN - Level 1 state
        this.areas = [];
        this.visibleAreas = [];
        this.areaSearch = "";
        this.selectedArea = null;
        this.areasAreLoading = true;
        // END - Level 1 state
        // BEGIN - Level 2 state
        this.contents = [];
        this.contentsAreLoading = false;
        // END - Level 2 state
        // BEGIN - Duplicated labels
        /**
         * Snapshots the label uniqueness check runs against, HIDDEN ROWS INCLUDED: the
         * back-end refuses a label held by a soft deleted node too, and that node is exactly
         * the one the user cannot see in the tree.
         *
         * They are NOT the lists being displayed: those follow the `includeDeleted` filter.
         */
        this.areasForValidation = [];
        this.contentsForValidation = [];
        /** Set when a snapshot could not be fetched: the check degrades, it never lies */
        this.areaCheckIsDegraded = false;
        this.contentCheckIsDegraded = false;
        // END - Duplicated labels
        /** Hidden (soft deleted) nodes are requested with includeDeleted=true */
        this.includeDeleted = false;
        /** Blocks drag&drop while a reorder is being persisted */
        this.reorderIsSaving = false;
        /** Blocks the destructive actions while their preview is being computed */
        this.previewIsLoading = false;
        /** Set while the destinations of a move are being fetched */
        this.moveIsLoading = false;
        this.rightPanel = "empty";
        this.newContentIsVisible = false;
        /** Identifiers (code + title) are read only until the user explicitly unlocks them */
        this.areaIdentifiersUnlocked = false;
        this.condoStructures = [];
        /** Builders of the Formly fields shared with the third level dialog */
        this.fieldFactory = new DocumentTemplateFieldFactory(this.translateService);
        // BEGIN - Area edit form
        this.areaForm = new FormGroup({});
        this.areaModel = {};
        this.areaFields = [];
        this.areaOptions = {};
        this.areaBtnOptions = this.buildSaveButton();
        // END - Area edit form
        // BEGIN - New area form
        this.newAreaForm = new FormGroup({});
        this.newAreaModel = {};
        this.newAreaFields = [];
        this.newAreaOptions = {};
        this.newAreaBtnOptions = this.buildSaveButton();
        // END - New area form
        // BEGIN - New content (section) form
        this.newContentForm = new FormGroup({});
        this.newContentModel = {};
        this.newContentFields = [];
        this.newContentOptions = {};
        this.newContentBtnOptions = this.buildSaveButton();
    }
    /**
     * Build a dedicated submit button state. Every form owns its own instance: sharing
     * one makes the spinner of a form spin while another one is saving.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildSaveButton = function () {
        return {
            active: false,
            text: this.translateService.instant("common.cta.save"),
            spinnerSize: 19,
            raised: true,
            stroked: false,
            flat: false,
            fab: false,
            buttonColor: "primary",
            spinnerColor: "primary",
            fullWidth: false,
            disabled: false,
            mode: "indeterminate",
            buttonIcon: {
                fontSet: "fal",
                fontIcon: "fa-save",
                inline: true,
            },
        };
    };
    /**
     * Init component
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.ngOnInit = function () {
        var _this = this;
        this.themeService.setPageTitle(this.translateService.instant("settings.documentTemplate.pageTitle"));
        this.subscriptions.push(this.authService.loggedUser$.subscribe(function (res) { return (_this.loggedUser = res); }));
        this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(function (res) { return (_this.loggedUserPermissions = res || []); }));
        this.lang =
            this.cookieService.get("lang") || this.configService.defaultLang;
    };
    /**
     * After view init logic
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.ngAfterViewInit = function () {
        this.loadAreas();
        this.loadCondoStructures();
    };
    /**
     * Handle component destroy
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    // BEGIN - Loading
    /**
     * Load level 1. Nothing else is fetched: sections arrive only when an area is
     * picked, documents only when the dialog opens.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.loadAreas = function () {
        var _this = this;
        this.areasAreLoading = true;
        this.subscriptions.push(this.documentTemplateService
            .listAreas(this.includeDeleted)
            .pipe(finalize(function () { return (_this.areasAreLoading = false); }))
            .subscribe(function (res) {
            // Already ordered by the back-end: the front-end never sorts the level
            _this.areas = res && Array.isArray(res.data) ? res.data : [];
            _this.applyAreaSearch();
            _this.refreshSelectedArea();
            _this.loadAreasForValidation();
        }, function (err) {
            _this.documentTemplateService.handleApiError(err);
        }));
    };
    /**
     * Refresh the snapshot the area label check runs against.
     *
     * Hidden areas have to be in it: the back-end refuses a label held by a soft deleted
     * area too, with its own wording, and that is the area the user cannot see. When the
     * displayed list already carries them there is nothing to fetch.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.loadAreasForValidation = function () {
        var _this = this;
        if (this.includeDeleted) {
            this.areasForValidation = this.areas;
            this.areaCheckIsDegraded = false;
            return;
        }
        this.subscriptions.push(this.documentTemplateService.listAreas(true).subscribe(function (res) {
            _this.areasForValidation =
                res && Array.isArray(res.data) ? res.data : [];
            _this.areaCheckIsDegraded = false;
        }, function () {
            // Never silent: the form says the check is unavailable, and the back-end
            // keeps refusing the duplicates it knows about.
            _this.areasForValidation = _this.areas;
            _this.areaCheckIsDegraded = true;
        }));
    };
    /**
     * Load the sections of the given area (level 2).
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.loadContents = function (areaId) {
        var _this = this;
        if (!areaId) {
            this.contents = [];
            this.contentsForValidation = [];
            return;
        }
        this.contentsAreLoading = true;
        this.subscriptions.push(this.documentTemplateService
            .listContents(areaId, this.includeDeleted)
            .pipe(finalize(function () { return (_this.contentsAreLoading = false); }))
            .subscribe(function (res) {
            _this.contents = res && Array.isArray(res.data) ? res.data : [];
            _this.loadContentsForValidation(areaId);
        }, function (err) {
            _this.contents = [];
            _this.documentTemplateService.handleApiError(err, {
                onNotFound: function () { return _this.loadAreas(); },
            });
        }));
    };
    /**
     * Refresh the snapshot the section label check runs against. @see
     * loadAreasForValidation: the section labels are unique inside their own area only.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.loadContentsForValidation = function (areaId) {
        var _this = this;
        if (this.includeDeleted) {
            this.contentsForValidation = this.contents;
            this.contentCheckIsDegraded = false;
            return;
        }
        this.subscriptions.push(this.documentTemplateService.listContents(areaId, true).subscribe(function (res) {
            _this.contentsForValidation =
                res && Array.isArray(res.data) ? res.data : [];
            _this.contentCheckIsDegraded = false;
        }, function () {
            _this.contentsForValidation = _this.contents;
            _this.contentCheckIsDegraded = true;
        }));
    };
    /**
     * Load the condo structures feeding the "structure" select of an array section.
     * The list is rebuilt from scratch on every call: appending to it is exactly the
     * bug that makes the legacy page accumulate duplicated options.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.loadCondoStructures = function () {
        var _this = this;
        this.subscriptions.push(this.documentTemplateService.getCondoStructures().subscribe(function (res) {
            _this.condoStructures =
                _this.documentTemplateService.extractStructureOptions(res);
        }, function (err) {
            _this.condoStructures = [];
            _this.documentTemplateService.handleApiError(err);
        }));
    };
    /**
     * Re-sync the selected area (and its editor) with the freshly loaded list, or drop
     * the selection when the area is gone.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.refreshSelectedArea = function () {
        var _this = this;
        if (!this.selectedArea) {
            return;
        }
        var found = this.areas.find(function (area) { return area._id.toString() === _this.selectedArea._id.toString(); });
        if (!found) {
            this.selectedArea = null;
            this.contents = [];
            this.rightPanel = "empty";
            this.newContentIsVisible = false;
            return;
        }
        this.selectedArea = found;
        if (this.rightPanel === "area") {
            this.buildAreaFields();
        }
    };
    // END - Loading
    // BEGIN - Filters
    /**
     * Toggle the "show hidden" filter: it drives includeDeleted on every list.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onIncludeDeletedChange = function () {
        this.loadAreas();
        if (this.selectedArea) {
            this.loadContents(this.selectedArea._id);
        }
    };
    /**
     * Filter the areas list client side (the level holds 23 rows, the back-end has no
     * search on it).
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.applyAreaSearch = function () {
        var query = (this.areaSearch || "").trim().toLowerCase();
        if (!query) {
            this.visibleAreas = this.areas.slice();
            return;
        }
        // `title` is deliberately out of the search: it is an internal identifier the user
        // never sees, and a row matching on it would look like a result out of nowhere
        this.visibleAreas = this.areas.filter(function (area) {
            var italian = area.translation ? area.translation.it || "" : "";
            var english = area.translation ? area.translation.en || "" : "";
            return (italian.toLowerCase().indexOf(query) >= 0 ||
                english.toLowerCase().indexOf(query) >= 0 ||
                (area.code || "").toLowerCase().indexOf(query) >= 0);
        });
    };
    Object.defineProperty(DocumentTemplateComponent.prototype, "areaDragIsDisabled", {
        /**
         * Reordering is only safe on the full list: the drop indexes refer to the FILTERED
         * list, while what gets sent is the whole sequence of ids, so with a filter on the
         * two would not describe the same order.
         *
         * @since 3.0.0
         */
        get: function () {
            return (this.reorderIsSaving || !!(this.areaSearch && this.areaSearch.trim()));
        },
        enumerable: true,
        configurable: true
    });
    // END - Filters
    // BEGIN - Level 1: selection and editing
    /**
     * Open an area: loads its sections and builds its editor.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onAreaClicked = function (area) {
        this.selectedArea = area;
        this.rightPanel = "area";
        this.newContentIsVisible = false;
        this.areaIdentifiersUnlocked = false;
        this.resetNewContentForm();
        this.buildAreaFields();
        this.loadContents(area._id);
    };
    /**
     * Build the area editor. Identifiers are part of the fields only once unlocked, so
     * that a locked form can never send them.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildAreaFields = function () {
        var area = this.selectedArea;
        if (!area) {
            return;
        }
        this.areaForm = new FormGroup({});
        this.areaModel = {
            code: area.code,
            group: area.group,
            type: area.type,
            visible: !!area.visible,
            expanded: !!area.expanded,
            translation: {
                it: area.translation ? area.translation.it : "",
                en: area.translation ? area.translation.en : "",
            },
        };
        var fields = [];
        if (this.areaIdentifiersUnlocked) {
            fields.push({
                fieldGroupClassName: "row",
                fieldGroup: [this.fieldFactory.areaCode("col-12 col-md-4")],
            });
        }
        fields.push({
            fieldGroupClassName: "row",
            fieldGroup: [
                this.fieldFactory.translation("it", "col-12 col-md-6", this.buildAreaLabelChecker("it", area._id)),
                this.fieldFactory.translation("en", "col-12 col-md-6", this.buildAreaLabelChecker("en", area._id)),
                this.fieldFactory.areaGroup("col-12 col-md-6"),
                this.fieldFactory.areaType("col-12 col-md-6"),
                this.fieldFactory.toggle("visible", "settings.documentTemplate.fields.visible", "col-12 col-md-6"),
                this.fieldFactory.toggle("expanded", "settings.documentTemplate.fields.expanded", "col-12 col-md-6"),
            ],
        });
        this.areaFields = fields;
    };
    /**
     * Ask for an explicit confirmation before letting the user touch `code`: it ends up
     * in already uploaded documents, in routes and in the QR codes already printed and
     * glued on the buildings.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onUnlockAreaIdentifiers = function () {
        var _this = this;
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: this.translateService.instant("settings.documentTemplate.dialogs.unlockIdentifier.title"),
                message: this.translateService.instant("settings.documentTemplate.dialogs.unlockIdentifier.message"),
                btnOkText: this.translateService.instant("settings.documentTemplate.actions.unlockIdentifier"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
                btnOkColor: "warn",
                btnOkIcon: "fa-unlock",
            },
            width: "560px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (confirmed) {
            if (!confirmed) {
                return;
            }
            _this.areaIdentifiersUnlocked = true;
            _this.buildAreaFields();
        });
    };
    /**
     * Save the area. Only the fields that really changed are sent, as the back-end
     * expects a partial payload.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onAreaSubmit = function () {
        var _this = this;
        if (!this.selectedArea) {
            return;
        }
        if (!this.markFormAsTouched(this.areaForm)) {
            return;
        }
        var payload = this.buildAreaUpdatePayload();
        if (!payload) {
            this.toastrService.info(this.translateService.instant("settings.documentTemplate.noChanges"));
            return;
        }
        var conflictFields = this.collectConflictFields(payload);
        // Touching an identifier is destructive outside this application: confirm again
        // right before writing it. The labels are not identifiers and go straight through.
        if (typeof payload.code !== "undefined") {
            this.confirmThen(this.translateService.instant("settings.documentTemplate.dialogs.saveIdentifier.title"), this.translateService.instant("settings.documentTemplate.dialogs.saveIdentifier.message"), function () { return _this.persistArea(payload, conflictFields); });
            return;
        }
        this.persistArea(payload, conflictFields);
    };
    /**
     * Perform the area PUT.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.persistArea = function (payload, conflictFields) {
        var _this = this;
        this.areaBtnOptions.active = true;
        this.subscriptions.push(this.documentTemplateService
            .updateArea(this.selectedArea._id, payload)
            .pipe(finalize(function () { return (_this.areaBtnOptions.active = false); }))
            .subscribe(function (res) {
            _this.toastrService.success(res && res.message
                ? res.message
                : _this.translateService.instant("settings.documentTemplate.saved"));
            _this.areaIdentifiersUnlocked = false;
            // Reloads the validation snapshot too: the label just saved is now taken
            _this.loadAreas();
        }, function (err) {
            _this.documentTemplateService.handleApiError(err, {
                form: _this.areaForm,
                conflictFields: conflictFields,
                onNotFound: function () { return _this.loadAreas(); },
            });
        }));
    };
    /**
     * Diff the area editor against the loaded area, so that the PUT carries only what
     * changed. Returns null when nothing changed.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildAreaUpdatePayload = function () {
        var area = this.selectedArea;
        var model = this.areaModel || {};
        var payload = {};
        var changed = false;
        if (this.areaIdentifiersUnlocked && model.code !== area.code) {
            payload.code = model.code;
            changed = true;
        }
        if (model.group !== area.group) {
            payload.group = model.group;
            changed = true;
        }
        if (model.type !== area.type) {
            payload.type = model.type;
            changed = true;
        }
        if (!!model.visible !== !!area.visible) {
            payload.visible = !!model.visible;
            changed = true;
        }
        if (!!model.expanded !== !!area.expanded) {
            payload.expanded = !!model.expanded;
            changed = true;
        }
        var translation = this.buildTranslationPayload(model.translation, area.translation);
        if (translation) {
            payload.translation = translation;
            changed = true;
        }
        return changed ? payload : null;
    };
    /**
     * Open the "new area" editor. Its form and model are dedicated: sharing them with
     * the detail editor is what makes the legacy page leak values between the two.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onAddAreaClicked = function () {
        this.rightPanel = "newArea";
        this.newContentIsVisible = false;
        this.newAreaForm = new FormGroup({});
        this.newAreaModel = {
            code: suggestAreaCode(this.areas.map(function (area) { return area.code; })),
            group: "amministrativo",
            type: "accordion",
            visible: true,
            expanded: false,
            translation: { it: "", en: "" },
        };
        this.newAreaFields = [
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    this.fieldFactory.areaCode("col-12 col-md-4"),
                    this.fieldFactory.translation("it", "col-12 col-md-4", this.buildAreaLabelChecker("it", "")),
                    this.fieldFactory.translation("en", "col-12 col-md-4", this.buildAreaLabelChecker("en", "")),
                    this.fieldFactory.areaGroup("col-12 col-md-6"),
                    this.fieldFactory.areaType("col-12 col-md-6"),
                    this.fieldFactory.toggle("visible", "settings.documentTemplate.fields.visible", "col-12 col-md-6"),
                    this.fieldFactory.toggle("expanded", "settings.documentTemplate.fields.expanded", "col-12 col-md-6"),
                ],
            },
        ];
    };
    /**
     * Create the area. `position` is omitted: the back-end appends it at the end, and
     * `title` is omitted too: the back-end generates it out of the english label.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onNewAreaSubmit = function () {
        var _this = this;
        if (!this.markFormAsTouched(this.newAreaForm)) {
            return;
        }
        var model = this.newAreaModel || {};
        this.newAreaBtnOptions.active = true;
        this.subscriptions.push(this.documentTemplateService
            .createArea({
            code: model.code,
            group: model.group,
            type: model.type,
            visible: !!model.visible,
            expanded: !!model.expanded,
            translation: {
                it: model.translation ? model.translation.it : "",
                en: model.translation ? model.translation.en : "",
            },
        })
            .pipe(finalize(function () { return (_this.newAreaBtnOptions.active = false); }))
            .subscribe(function (res) {
            _this.toastrService.success(res && res.message
                ? res.message
                : _this.translateService.instant("settings.documentTemplate.created"));
            _this.loadAreas();
            var created = res && res.data ? res.data : null;
            if (created && created._id) {
                // Jump straight into the freshly created area, ready for its sections
                _this.onAreaClicked(created);
            }
            else {
                _this.rightPanel = "empty";
            }
        }, function (err) {
            _this.documentTemplateService.handleApiError(err, {
                form: _this.newAreaForm,
                conflictFields: ["code", "translation.it", "translation.en"],
            });
        }));
    };
    /**
     * Hide an area. The delete cascades on its sections and documents, so the exact
     * amount of nodes about to disappear is computed first (a single /tree call) and
     * shown inside the confirmation.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onDeleteArea = function (area) {
        var _this = this;
        this.previewIsLoading = true;
        this.subscriptions.push(this.documentTemplateService
            .getTree(false)
            .pipe(finalize(function () { return (_this.previewIsLoading = false); }))
            .subscribe(function (res) {
            _this.confirmDeleteArea(area, _this.countAreaSubtree(res, area._id));
        }, function () {
            // The preview is a nicety: a failure must not block the deletion, it only
            // downgrades the confirmation to its generic wording.
            _this.confirmDeleteArea(area, null);
        }));
    };
    /**
     * Count the sections and documents a cascading delete would hide.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.countAreaSubtree = function (res, areaId) {
        var areas = res && res.data && Array.isArray(res.data.area) ? res.data.area : [];
        var area = areas.find(function (item) {
            return item && item._id && item._id.toString() === areaId.toString();
        });
        if (!area || !Array.isArray(area.content)) {
            return { contents: 0, docs: 0 };
        }
        var docs = 0;
        area.content.forEach(function (content) {
            if (content && Array.isArray(content.documents)) {
                docs += content.documents.length;
            }
        });
        return { contents: area.content.length, docs: docs };
    };
    /**
     * Confirmation of the area deletion, then the DELETE itself.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.confirmDeleteArea = function (area, counts) {
        var _this = this;
        var label = this.nodeLabel(area);
        var message = counts
            ? this.translateService.instant("settings.documentTemplate.dialogs.deleteArea.messageWithCounts", { name: label, contents: counts.contents, docs: counts.docs })
            : this.translateService.instant("settings.documentTemplate.dialogs.deleteArea.message", { name: label });
        this.confirmThen(this.translateService.instant("settings.documentTemplate.dialogs.deleteArea.title"), message, function () {
            _this.subscriptions.push(_this.documentTemplateService.deleteArea(area._id).subscribe(function (res) {
                _this.notifyDeleteResult(res);
                _this.loadAreas();
                if (_this.selectedArea &&
                    _this.selectedArea._id.toString() === area._id.toString()) {
                    _this.loadContents(area._id);
                }
            }, function (err) {
                _this.documentTemplateService.handleApiError(err, {
                    onNotFound: function () { return _this.loadAreas(); },
                });
            }));
        }, "warn");
    };
    /**
     * Restore a hidden area. The restore is NOT cascading: its sections and documents
     * stay hidden and have to be restored one by one.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onRestoreArea = function (area) {
        var _this = this;
        this.subscriptions.push(this.documentTemplateService.restoreArea(area._id).subscribe(function (res) {
            // Body carries the "restore is not cascading" reminder, title the back-end
            // message: toastr renders the title on top, so the short text goes there.
            _this.toastrService.success(_this.translateService.instant("settings.documentTemplate.restoreNotCascadingHint"), res && res.message
                ? res.message
                : _this.translateService.instant("settings.documentTemplate.restored"));
            _this.loadAreas();
            if (_this.selectedArea &&
                _this.selectedArea._id.toString() === area._id.toString()) {
                _this.loadContents(area._id);
            }
        }, function (err) {
            _this.documentTemplateService.handleApiError(err, {
                onNotFound: function () { return _this.loadAreas(); },
            });
        }));
    };
    /**
     * Persist the new order of level 1. One PUT on the moved node when the sparse
     * numbering leaves room between its neighbours, a full renumbering otherwise.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onAreaDropped = function (event) {
        var _this = this;
        if (event.previousIndex === event.currentIndex) {
            return;
        }
        // Optimistic reorder, then let the back-end persist the whole sequence at once.
        moveItemInArray(this.areas, event.previousIndex, event.currentIndex);
        this.applyAreaSearch();
        this.reorderIsSaving = true;
        this.subscriptions.push(this.documentTemplateService
            .reorderAreas(this.areas.map(function (area) { return area._id; }), this.includeDeleted)
            .pipe(finalize(function () { return (_this.reorderIsSaving = false); }))
            .subscribe(function (res) {
            // The response IS the level, already sorted: take it as the truth
            if (res && Array.isArray(res.data)) {
                _this.areas = res.data;
                _this.applyAreaSearch();
                _this.syncSelectedArea();
            }
            _this.toastrService.success(_this.translateService.instant("settings.documentTemplate.reordered"));
        }, function (err) {
            _this.documentTemplateService.handleApiError(err);
            _this.loadAreas();
        }));
    };
    /**
     * Re-point the selected area at its instance inside a freshly returned list, WITHOUT
     * rebuilding the editor: a reorder must not throw away what the user is typing.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.syncSelectedArea = function () {
        var _this = this;
        if (!this.selectedArea) {
            return;
        }
        var found = this.areas.find(function (area) { return area._id.toString() === _this.selectedArea._id.toString(); });
        if (found) {
            this.selectedArea = found;
        }
    };
    // END - Level 1: selection and editing
    // BEGIN - Level 2: sections
    /**
     * Open the third level dialog of a section. The dialog owns the section editor too
     * (translation, structure, visibility), exactly like the legacy page.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onContentClicked = function (content) {
        var _this = this;
        // Angular CDK 8's drag&drop computes pointer position relative to the window scroll
        // captured when the drag starts; if the page behind the dialog is scrolled down,
        // that offset desyncs the calculation and breaks reordering inside the dialog.
        window.scrollTo(0, 0);
        var dialogRef = this.dialog.open(DocumentTemplateDocsDialogComponent, {
            data: {
                content: content,
                area: this.selectedArea,
                contents: this.contents,
                condoStructures: this.condoStructures,
                includeDeleted: this.includeDeleted,
            },
            width: "90%",
            maxWidth: "90vw",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (res) {
            // Only the section list can have changed from in there: reload that one level.
            if (res && res.changed && _this.selectedArea) {
                _this.loadContents(_this.selectedArea._id);
            }
        });
    };
    /**
     * Open the "new section" editor, with its own form instance.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onAddContentClicked = function () {
        if (!this.selectedArea) {
            return;
        }
        this.newContentIsVisible = true;
        this.resetNewContentForm();
    };
    /**
     * (Re)build the "new section" form from scratch.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.resetNewContentForm = function () {
        var _this = this;
        this.newContentForm = new FormGroup({});
        this.newContentModel = {
            code: "",
            elementType: "object",
            structure: "",
            visible: true,
            expanded: false,
            translation: { it: "", en: "" },
        };
        this.newContentFields = [
            {
                fieldGroupClassName: "row",
                fieldGroup: [
                    this.fieldFactory.contentCode("col-12 col-md-4"),
                    this.fieldFactory.translation("it", "col-12 col-md-4", this.buildContentLabelChecker("it", "")),
                    this.fieldFactory.translation("en", "col-12 col-md-4", this.buildContentLabelChecker("en", "")),
                    this.fieldFactory.elementType("col-12 col-md-6"),
                    this.fieldFactory.structure("col-12 col-md-6", this.buildStructureOptions(), function () { return _this.newContentModel; }),
                    this.fieldFactory.toggle("visible", "settings.documentTemplate.fields.visible", "col-12 col-md-6"),
                    this.fieldFactory.toggle("expanded", "settings.documentTemplate.fields.expanded", "col-12 col-md-6"),
                ],
            },
        ];
    };
    /**
     * Create the section under the selected area. `title` is omitted: the back-end
     * generates it out of the english label alone.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onNewContentSubmit = function () {
        var _this = this;
        if (!this.selectedArea) {
            return;
        }
        if (!this.markFormAsTouched(this.newContentForm)) {
            return;
        }
        var model = this.newContentModel || {};
        var isArray = model.elementType === "array";
        if (isArray && !model.structure) {
            var control = this.newContentForm.get("structure");
            if (control) {
                control.markAsTouched();
                control.setErrors({
                    "server-error": this.translateService.instant("common.errors.field_is_required"),
                });
            }
            this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"));
            return;
        }
        this.newContentBtnOptions.active = true;
        this.subscriptions.push(this.documentTemplateService
            .createContent({
            areaId: this.selectedArea._id,
            code: model.code,
            elementType: model.elementType,
            structure: isArray ? model.structure : "",
            visible: !!model.visible,
            expanded: !!model.expanded,
            translation: {
                it: model.translation ? model.translation.it : "",
                en: model.translation ? model.translation.en : "",
            },
        })
            .pipe(finalize(function () { return (_this.newContentBtnOptions.active = false); }))
            .subscribe(function (res) {
            _this.toastrService.success(res && res.message
                ? res.message
                : _this.translateService.instant("settings.documentTemplate.created"));
            _this.newContentIsVisible = false;
            _this.loadContents(_this.selectedArea._id);
        }, function (err) {
            _this.documentTemplateService.handleApiError(err, {
                form: _this.newContentForm,
                conflictFields: ["code", "translation.it", "translation.en"],
            });
        }));
    };
    /**
     * Hide a section. The delete cascades on its documents, counted first.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onDeleteContent = function (content) {
        var _this = this;
        this.previewIsLoading = true;
        this.subscriptions.push(this.documentTemplateService
            .listDocs(content._id, false)
            .pipe(finalize(function () { return (_this.previewIsLoading = false); }))
            .subscribe(function (res) {
            var docs = res && Array.isArray(res.data) ? res.data.length : 0;
            _this.confirmDeleteContent(content, docs);
        }, function () {
            _this.confirmDeleteContent(content, null);
        }));
    };
    /**
     * Confirmation of the section deletion, then the DELETE itself.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.confirmDeleteContent = function (content, docs) {
        var _this = this;
        var label = this.nodeLabel(content);
        var message = docs === null
            ? this.translateService.instant("settings.documentTemplate.dialogs.deleteContent.message", { name: label })
            : this.translateService.instant("settings.documentTemplate.dialogs.deleteContent.messageWithCounts", { name: label, docs: docs });
        this.confirmThen(this.translateService.instant("settings.documentTemplate.dialogs.deleteContent.title"), message, function () {
            _this.subscriptions.push(_this.documentTemplateService.deleteContent(content._id).subscribe(function (res) {
                _this.notifyDeleteResult(res);
                _this.loadContents(_this.selectedArea._id);
            }, function (err) {
                _this.documentTemplateService.handleApiError(err, {
                    onNotFound: function () { return _this.loadContents(_this.selectedArea._id); },
                });
            }));
        }, "warn");
    };
    /**
     * Restore a hidden section. Blocked (and the button disabled) while its area is
     * hidden: the back-end answers 400 in that case.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onRestoreContent = function (content) {
        var _this = this;
        this.subscriptions.push(this.documentTemplateService.restoreContent(content._id).subscribe(function (res) {
            // Body carries the "restore is not cascading" reminder, title the back-end
            // message: toastr renders the title on top, so the short text goes there.
            _this.toastrService.success(_this.translateService.instant("settings.documentTemplate.restoreNotCascadingHint"), res && res.message
                ? res.message
                : _this.translateService.instant("settings.documentTemplate.restored"));
            _this.loadContents(_this.selectedArea._id);
        }, function (err) {
            _this.documentTemplateService.handleApiError(err, {
                onNotFound: function () { return _this.loadContents(_this.selectedArea._id); },
            });
        }));
    };
    /**
     * True when the section cannot be restored because its area is still hidden.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.restoreContentIsBlocked = function () {
        return !!(this.selectedArea && this.selectedArea.deleted);
    };
    /**
     * Move a section into another area: a single PUT with `{ areaId }`. Without a
     * position the section lands at the end of the destination.
     *
     * The labels of a section are unique INSIDE ITS AREA, so a destination already
     * holding a section with the same name refuses the move with a 409. The sections of
     * the other areas are not in memory: the whole tree is fetched once, on demand, only
     * for this deliberate action, and the destinations that would be refused are offered
     * disabled instead of letting the user discover it after the fact.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onMoveContent = function (content) {
        var _this = this;
        this.moveIsLoading = true;
        this.subscriptions.push(this.documentTemplateService
            .getTree(true)
            .pipe(finalize(function () { return (_this.moveIsLoading = false); }))
            .subscribe(function (res) {
            _this.openMoveContentDialog(content, _this.readTreeAreas(res));
        }, function () {
            // The check is a nicety: a failure must not block the move, the back-end
            // stays the one refusing a destination whose name is taken.
            _this.openMoveContentDialog(content, null);
        }));
    };
    /**
     * Sections of every area, indexed by area id, out of a GET /tree response.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.readTreeAreas = function (res) {
        return res && res.data && Array.isArray(res.data.area) ? res.data.area : [];
    };
    /**
     * Open the destination picker of a section move and perform the move.
     *
     * @since 3.0.0
     *
     * @param content   Section being moved
     * @param treeAreas Whole tree, or null when it could not be fetched: every
     *                  destination is then offered, and the back-end arbitrates
     */
    DocumentTemplateComponent.prototype.openMoveContentDialog = function (content, treeAreas) {
        var _this = this;
        var takenHint = this.translateService.instant("settings.documentTemplate.move.destLabelTaken");
        var options = this.areas
            .filter(function (area) { return area._id.toString() !== _this.selectedArea._id.toString(); })
            .map(function (area) {
            var taken = treeAreas
                ? _this.labelIsTakenInArea(content, area._id, treeAreas)
                : false;
            return {
                value: area._id,
                label: _this.nodeLabel(area),
                hint: taken ? area.code + " \u2014 " + takenHint : area.code,
                disabled: taken,
            };
        });
        var data = {
            title: this.translateService.instant("settings.documentTemplate.move.contentTitle"),
            subject: this.nodeLabel(content),
            currentLabel: this.nodeLabel(this.selectedArea),
            selectLabel: this.translateService.instant("settings.documentTemplate.move.destArea"),
            warning: this.translateService.instant("settings.documentTemplate.move.warning"),
            groups: [{ label: "", options: options }],
        };
        var dialogRef = this.dialog.open(DocumentTemplateMoveDialogComponent, {
            data: data,
            width: "560px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (destAreaId) {
            if (!destAreaId) {
                return;
            }
            var payload = {
                areaId: destAreaId,
            };
            _this.subscriptions.push(_this.documentTemplateService
                .updateContent(content._id, payload)
                .subscribe(function (res) {
                _this.toastrService.success(res && res.message
                    ? res.message
                    : _this.translateService.instant("settings.documentTemplate.moved"));
                _this.loadContents(_this.selectedArea._id);
            }, function (err) {
                _this.documentTemplateService.handleApiError(err, {
                    onNotFound: function () { return _this.loadContents(_this.selectedArea._id); },
                });
            }));
        });
    };
    /**
     * Persist the new order of level 2.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.onContentDropped = function (event) {
        var _this = this;
        if (event.previousIndex === event.currentIndex || !this.selectedArea) {
            return;
        }
        moveItemInArray(this.contents, event.previousIndex, event.currentIndex);
        this.reorderIsSaving = true;
        this.subscriptions.push(this.documentTemplateService
            .reorderContents(this.selectedArea._id, this.contents.map(function (content) { return content._id; }), this.includeDeleted)
            .pipe(finalize(function () { return (_this.reorderIsSaving = false); }))
            .subscribe(function (res) {
            if (res && Array.isArray(res.data)) {
                _this.contents = res.data;
            }
            _this.toastrService.success(_this.translateService.instant("settings.documentTemplate.reordered"));
        }, function (err) {
            _this.documentTemplateService.handleApiError(err);
            _this.loadContents(_this.selectedArea._id);
        }));
    };
    // END - Level 2: sections
    // BEGIN - Formly field factories
    /**
     * Structure options rebuilt from scratch on every call. Appending to a shared array
     * is exactly what makes the legacy page accumulate duplicated options.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildStructureOptions = function () {
        return this.condoStructures.map(function (structure) { return ({
            value: structure.key,
            label: structure.translation
                ? structure.translation.it || structure.key
                : structure.key,
        }); });
    };
    // END - Formly field factories
    // BEGIN - Duplicated labels
    /**
     * Label check of an area: the italian and the english labels are unique across ALL
     * the areas, hidden ones included.
     *
     * The snapshot is read when the validator runs, not when it is built, so a form left
     * open while another area is created still validates against the current state.
     *
     * @since 3.0.0
     *
     * @param language    Label being validated
     * @param [excludeId] Area being edited, which must not collide with itself
     */
    DocumentTemplateComponent.prototype.buildAreaLabelChecker = function (language, excludeId) {
        var _this = this;
        return function (value) {
            return _this.buildConflictMessage(findLabelConflict(_this.areasForValidation, language, value, excludeId), "Area");
        };
    };
    /**
     * Label check of a section: unique INSIDE ITS AREA only. Two areas legitimately hold
     * a section with the same name ("Sicurezza" lives in more than one area).
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildContentLabelChecker = function (language, excludeId) {
        var _this = this;
        return function (value) {
            return _this.buildConflictMessage(findLabelConflict(_this.contentsForValidation, language, value, excludeId), "Content");
        };
    };
    /**
     * Wording of a label conflict, '' when there is none.
     *
     * A hidden owner gets its own message: the user does not see that node in the tree,
     * so the name looks free and a plain "already used" would read as a bug.
     *
     * @since 3.0.0
     *
     * @param conflict Colliding node, or null
     * @param level    'Area' or 'Content', to pick the wording of the right level
     */
    DocumentTemplateComponent.prototype.buildConflictMessage = function (conflict, level) {
        if (!conflict) {
            return "";
        }
        var key = conflict.deleted
            ? "settings.documentTemplate.errors.duplicate" + level + "Hidden"
            : "settings.documentTemplate.errors.duplicate" + level;
        return this.translateService.instant(key, { name: conflict.label });
    };
    /**
     * True when the destination area already holds a section carrying one of the labels
     * of the section being moved: the back-end refuses that move with a 409.
     *
     * @since 3.0.0
     *
     * @param content   Section being moved
     * @param areaId    Candidate destination
     * @param treeAreas Whole tree, hidden nodes included
     */
    DocumentTemplateComponent.prototype.labelIsTakenInArea = function (content, areaId, treeAreas) {
        var area = treeAreas.find(function (item) { return item && item._id && item._id.toString() === areaId.toString(); });
        if (!area || !Array.isArray(area.content) || !area.content.length) {
            return false;
        }
        var translation = content.translation ? content.translation : { it: "", en: "" };
        // The section keeps its own id in another area: nothing to exclude here
        return (!!findLabelConflict(area.content, "it", translation.it) ||
            !!findLabelConflict(area.content, "en", translation.en));
    };
    /**
     * Controls of a payload that could be the duplicated one, so that a 409 highlights
     * where to act: the identifier and the two labels, whichever is actually being sent.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.collectConflictFields = function (payload) {
        var fields = [];
        if (typeof payload.code !== "undefined") {
            fields.push("code");
        }
        var translation = payload.translation;
        if (translation) {
            if (typeof translation.it !== "undefined") {
                fields.push("translation.it");
            }
            if (typeof translation.en !== "undefined") {
                fields.push("translation.en");
            }
        }
        return fields;
    };
    // END - Duplicated labels
    // BEGIN - Shared helpers
    /**
     * Label of a node in the current interface language, with a fallback on the other
     * language and then on the identifier the user does know (the code, or the document
     * key). `title` is never used: it is an internal identifier the user must not see.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.nodeLabel = function (node) {
        if (!node) {
            return "";
        }
        var translation = node.translation || {};
        if (this.lang === "en" && translation.en) {
            return translation.en;
        }
        if (translation.it) {
            return translation.it;
        }
        if (translation.en) {
            return translation.en;
        }
        return node.code || node.key || "";
    };
    /**
     * Mark every control as touched and refuse an invalid submit, always telling the
     * user why (the legacy page only writes to the console).
     *
     * @since 3.0.0
     *
     * @returns true when the form can be submitted
     */
    DocumentTemplateComponent.prototype.markFormAsTouched = function (form) {
        if (!form) {
            return false;
        }
        Object.keys(form.controls).forEach(function (key) {
            form.controls[key].markAsTouched();
            form.controls[key].markAsDirty();
        });
        if (form.invalid) {
            this.toastrService.error(this.translateService.instant("common.errors.form_is_invalid"));
            return false;
        }
        return true;
    };
    /**
     * Build the translation part of an update payload, keeping only the languages that
     * really changed (the back-end merges, it does not replace).
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.buildTranslationPayload = function (model, current) {
        var source = model || {};
        var original = current || {};
        var payload = {};
        var changed = false;
        if (source.it !== original.it) {
            payload.it = source.it;
            changed = true;
        }
        if (source.en !== original.en) {
            payload.en = source.en;
            changed = true;
        }
        return changed ? payload : null;
    };
    /**
     * Open a confirmation dialog and run `action` when the user confirms.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.confirmThen = function (title, message, action, okColor) {
        var dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: title,
                message: message,
                btnOkText: this.translateService.instant("common.cta.confirm"),
                btnCancelText: this.translateService.instant("common.cta.undo"),
                btnOkColor: okColor || "primary",
            },
            width: "560px",
        });
        dialogRef
            .beforeClosed()
            .pipe(take(1))
            .subscribe(function (confirmed) {
            if (confirmed) {
                action();
            }
        });
    };
    /**
     * Report what a cascading delete really hid, using the counters of the response.
     *
     * @since 3.0.0
     */
    DocumentTemplateComponent.prototype.notifyDeleteResult = function (res) {
        var data = res && res.data ? res.data : { deletedAreas: 0, deletedContents: 0, deletedDocs: 0 };
        this.toastrService.success(this.translateService.instant("settings.documentTemplate.deleteResult", {
            areas: data.deletedAreas || 0,
            contents: data.deletedContents || 0,
            docs: data.deletedDocs || 0,
        }), res && res.message ? res.message : undefined);
    };
    return DocumentTemplateComponent;
}());
export { DocumentTemplateComponent };
